/* =============================================================================
   VIEWS / ADMIN / SETTINGS-RULES — Regeln-Sub-Tab (3-Pfad: Reservierung/Bestellung/Lieferung)
   =============================================================================
   S79 REF-6: aus settings.js extrahiert (Subsection-Renderer des „Regeln"-
   Sub-Tabs):
     - renderRulesReservationsSubsection()  Reservierungs-Regeln + DSGVO + Adjazenz
     - renderRulesOrdersSubsection()         Bestell-Regeln + Steuer + Bezahlmethoden
     - renderRulesDeliverySubsection()       Liefer-Regeln (Wrapper um settings-delivery-rules.js)

   Mitgenommen wurden die nur hier genutzten Order-Helfer (orderDefaults,
   getOrderSetting, PAYMENT_METHOD_LABELS). Der dünne Dispatcher
   `renderRulesSection()` + die Sub-Sub-Tab-Bar bleiben im Haupt-`settings.js`
   (Cross-Cutting, Anti-Circular — vgl. settings-delivery-rules.js).

   Inline-Handler (updateSetting / updateSettingClampedInt / updateSettingMinInt /
   updateOrderPaymentMethod) resolven zur Laufzeit über window. State über das
   lokale getState() (window.App.storage._getInternalState()).
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { spontaneousReserveSeats } from '../../core/capacity.js';
import { renderDeliveryRulesCard } from './settings-delivery-rules.js';
// S243 Cut A — Defaults des Tisch-QR-Features an EINER Stelle (Spiegel der
// SQL-Defaults in phase_176).
import { selfOrderSetting } from '../../core/table-qr.js';
// S266 — Gast-Kanäle einzeln schaltbar (phase_196): Karte „Gast-Kanäle" über
// der Sub-Tab-Leiste; ersetzt den Master-Schalter `orderingEnabled`.
import {
  GUEST_CHANNEL_KEYS,
  resolvedGuestChannels,
  guestChannelEnabled,
  hasDeliveryPath
} from '../../core/guest-channels.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

// -----------------------------------------------------------------------------
// Hilfsgetter — robust gegen fehlende Defaults (greift, wenn die DB-Migration
// die Order-Defaults bei einem Tenant noch nicht angelegt hat).
// -----------------------------------------------------------------------------
function orderDefaults() {
  return {
    orderLeadTimeMin:   30,
    orderPrepTimeMin:   15,
    orderSlotCapacity:  4,
    orderSoundEnabled:  true,
    orderPushEnabled:   false,
    receiptPromptOnPaid: true,
    taxRateFood:        7,
    taxRateDrinks:      19,
    invoicePrefix:      'GG-',
    paymentMethods:     ['cash', 'card']
  };
}

function getOrderSetting(key) {
  const state = getState();
  const s = (state && state.settings) || {};
  const d = orderDefaults();
  return (Object.prototype.hasOwnProperty.call(s, key) ? s[key] : d[key]);
}

const PAYMENT_METHOD_LABELS = {
  cash:   'Bar',
  card:   'Karte (vor Ort)',
  stripe: 'Online (Stripe)'
};

export function renderRulesReservationsSubsection() {
  const state = getState();
  const s = state.settings;
  return `
    <div class="settings-grid">
      <div class="card">
        <h2>Reservierungs-Regeln</h2>
        <div class="field">
          <label>Manuelle Bestätigung ab</label>
          <input type="number" min="1" max="30" value="${s.manualConfirmThreshold}" oninput="updateSetting('manualConfirmThreshold', parseInt(this.value,10) || 1)">
          <div class="hint">Anfragen mit so vielen Personen oder mehr brauchen deine manuelle Bestätigung.</div>
        </div>
        <div class="field">
          <label>Spontan-Reserve (%)</label>
          <input type="number" min="0" max="100" value="${s.spontaneousReservePercent}" oninput="updateSetting('spontaneousReservePercent', parseInt(this.value,10) || 0)">
          <div class="hint">Dieser Anteil der Plätze bleibt für Walk-Ins frei. Aktuell: ${spontaneousReserveSeats()} Plätze.</div>
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">Außenbereich</div>
            <div class="desc">Reservierungen für draußen sind möglich.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${s.outdoorEnabled ? 'checked' : ''} onchange="updateSetting('outdoorEnabled', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">Clubraum</div>
            <div class="desc">Reservierungen für den Clubraum sind möglich.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${s.clubEnabled ? 'checked' : ''} onchange="updateSetting('clubEnabled', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">Online-Stornierung durch Kunden</div>
            <div class="desc">Kunden können ihre Reservierung über den Link in der Bestätigungsmail selbst stornieren.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${s.allowCustomerCancel ? 'checked' : ''} onchange="updateSetting('allowCustomerCancel', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">Warteliste</div>
            <div class="desc">Wenn ausgebucht, können Kunden sich auf eine Warteliste setzen.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${s.allowWaitlist ? 'checked' : ''} onchange="updateSetting('allowWaitlist', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">DSGVO-Einverständnis erforderlich</div>
            <div class="desc">Kunde muss vor Absenden eine Checkbox bestätigen.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${s.requireGdprConsent ? 'checked' : ''} onchange="updateSetting('requireGdprConsent', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="field" style="margin-top: 12px;">
          <label>Link zur Datenschutzerklärung (optional)</label>
          <input type="url" value="${htmlEscape(s.gdprPolicyUrl || '')}" oninput="updateSetting('gdprPolicyUrl', this.value)" placeholder="https://...">
          <div class="hint">Wenn gesetzt, erscheint im Reservierungsformular ein "Mehr erfahren"-Link.</div>
        </div>
        <div class="field" style="margin-top: 12px;">
          <label>Adjazenz-Schwellwert (Pixel)</label>
          <input type="number" min="20" max="200" step="5" value="${s.adjacencyThresholdPx}" oninput="updateSetting('adjacencyThresholdPx', parseInt(this.value,10) || 55)">
          <div class="hint">Wie nah müssen Tische im Layout zueinander stehen, damit sie als zusammenschiebbar gelten? Default: 55 px (≈ halbe Tisch-Breite).</div>
        </div>
      </div>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Sub-Tab: Bestell-Regeln + Bezahlmethoden (S62 Hotfix-3)
// -----------------------------------------------------------------------------
export function renderRulesOrdersSubsection() {
  const state = getState();
  const s = state.settings;
  const paymentMethods = Array.isArray(s.paymentMethods) ? s.paymentMethods : orderDefaults().paymentMethods;
  return `
    <div class="settings-grid">
      <div class="card">
        <h2>Bestell-Regeln <span class="badge-soft" style="font-size:.7rem;">Takeaway</span></h2>
        <div class="hint" style="margin-bottom:10px;">
          Ob Gäste online bestellen können, schaltest du oben in der Karte
          <strong>„Gast-Kanäle"</strong> (Mitnehmen · Lieferung · Bestellen am Tisch).
        </div>
        <div class="field-row">
          <div class="field">
            <label>Mindest-Vorlaufzeit (Min.)</label>
            <input type="number" min="0" max="240" step="5" value="${getOrderSetting('orderLeadTimeMin')}" oninput="updateSettingClampedInt('orderLeadTimeMin', this.value, 0, 240, 0)">
            <div class="hint">So weit muss die Abholzeit mindestens in der Zukunft liegen. Wird serverseitig erzwungen.</div>
          </div>
          <div class="field">
            <label>Standard-Zubereitungszeit (Min.)</label>
            <input type="number" min="0" max="120" step="5" value="${getOrderSetting('orderPrepTimeMin')}" oninput="updateSettingClampedInt('orderPrepTimeMin', this.value, 0, 120, 0)">
            <div class="hint">Richtwert für die Kundenkommunikation und Slot-Reservierung.</div>
          </div>
        </div>
        <div class="field">
          <label>Max. parallele Bestellungen pro 15-Min-Slot</label>
          <input type="number" min="1" max="50" step="1" value="${getOrderSetting('orderSlotCapacity')}" oninput="updateSettingClampedInt('orderSlotCapacity', this.value, 1, 50, 1)">
          <div class="hint">Schutz vor Überbuchung: ist der Slot voll, wird die nächste Abholzeit vorgeschlagen.</div>
        </div>

        <h3 style="margin-top:16px;font-size:1rem;">Notifications im Cockpit</h3>
        <div class="toggle-row">
          <div>
            <div class="label">Sound-Ping bei neuer Bestellung</div>
            <div class="desc">Default-Wert für neue Cockpit-Sessions. Mitarbeiter können das je Browser oben in der Toolbar überschreiben.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${getOrderSetting('orderSoundEnabled') ? 'checked' : ''} onchange="updateSetting('orderSoundEnabled', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">Browser-Push als Default vorschlagen</div>
            <div class="desc">Schaltet den Aktivieren-Button im Cockpit-Header sichtbar. Permission wird erst auf Klick angefragt.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${getOrderSetting('orderPushEnabled') ? 'checked' : ''} onchange="updateSetting('orderPushEnabled', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>

        <h3 style="margin-top:16px;font-size:1rem;">Belegausgabepflicht</h3>
        <div class="hint" style="margin-bottom:8px;">
          Seit § 146a AO muss dem Kunden bei jeder Bezahlung ein Beleg
          zur Verfügung gestellt werden. Mit aktivem Toggle erscheint nach jedem
          «Bezahlt»-Klick ein Modal mit vier Optionen: <strong>Versenden</strong>
          (Mail an Kunde), <strong>Drucken</strong> (Bondrucker oder
          Browser-Druck-Vorschau), <strong>Archivieren</strong>
          (lokales Bons-Archiv) und <strong>Bon anzeigen</strong> (Vorschau).
          Phase 21 (Session 22): das gleiche Modal wird jetzt auch über den
          „Beleg-Aktion"-Button im Bestellungs-Detail aufgerufen sowie nach
          jedem Stornobeleg — einheitlich statt willkürlich aufpoppender
          Browser-Druck-Vorschau.
        </div>
        <div class="toggle-row">
          <div>
            <div class="label">Beleg-Aktion nach «Bezahlt»-Klick anfragen</div>
            <div class="desc">Deaktivieren nur, wenn die Belegausgabe bereits über einen separaten Workflow läuft (z.B. Hardware-Bondrucker mit Auto-Auslöser — siehe Einstellungen → Hardware → Drucker → „Bon automatisch drucken nach Bezahlt"). Wenn beides aktiv ist, läuft der Hardware-Druck zusätzlich zum Modal.</div>
          </div>
          <label class="toggle">
            <input type="checkbox" ${getOrderSetting('receiptPromptOnPaid') ? 'checked' : ''} onchange="updateSetting('receiptPromptOnPaid', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>

        <h3 style="margin-top:16px;font-size:1rem;">Buchhaltung &amp; Steuer</h3>
        <div class="field-row">
          <div class="field">
            <label>MwSt-Satz Speisen (%)</label>
            <input type="number" min="0" max="25" step="1" value="${getOrderSetting('taxRateFood')}" oninput="updateSettingClampedInt('taxRateFood', this.value, 0, 25, 0)">
            <div class="hint">DE-Standard: 7%. Wird pro Position serverseitig angewendet.</div>
          </div>
          <div class="field">
            <label>MwSt-Satz Getränke (%)</label>
            <input type="number" min="0" max="25" step="1" value="${getOrderSetting('taxRateDrinks')}" oninput="updateSettingClampedInt('taxRateDrinks', this.value, 0, 25, 0)">
            <div class="hint">DE-Standard: 19%. Greift bei Items mit Kategorie „Getränke".</div>
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label>Rechnungsnummer-Präfix</label>
            <input type="text" value="${htmlEscape(getOrderSetting('invoicePrefix') || '')}" oninput="updateSetting('invoicePrefix', this.value)" placeholder="GG-">
            <div class="hint">Reiner Marken-Präfix <strong>ohne Jahr</strong>. Beispiel: <code>GG-</code> → <code>GG-2026-06-26-001</code>.</div>
          </div>
          <div class="field">
            <label>Belegnummern-Schema</label>
            <div class="hint" style="padding:8px 10px; background:var(--bg-soft); border-radius:6px; margin-top:2px;">
              <strong>Täglich ab 001</strong> — Reset pro Z-Bon-Zahltag (Zeitzone Europe/Berlin). Format
              <code>&lt;Präfix&gt;JJJJ-MM-TT-NNN</code>. Server-verwaltet, atomar pro Bezahlung hochgezählt; eine
              bereits vergebene Nummer kann nicht erneut gesetzt werden (GoBD §14 UStG). Keine manuelle Vergabe nötig.
            </div>
          </div>
        </div>

        <h3 style="margin-top:16px;font-size:1rem;">Bezahlmethoden</h3>
        <div class="hint" style="margin-bottom:8px;">Welche Methoden sollen im Cockpit-Bezahl-Dialog erscheinen?</div>
        ${['cash','card','stripe'].map(m => `
          <div class="toggle-row">
            <div style="min-width:0; flex: 1 1 auto;">
              <div class="label">${PAYMENT_METHOD_LABELS[m]}</div>
              ${m === 'stripe' ? '<div class="desc">Aktiviert den «Online bezahlen»-Button in den Gast-Erfolgsdialogen (Kanäle unten wählbar).</div>' : ''}
            </div>
            <label class="toggle" style="flex: 0 0 auto;">
              <input type="checkbox" ${paymentMethods.includes(m) ? 'checked' : ''} onchange="updateOrderPaymentMethod('${m}', this.checked)">
              <span class="toggle-slider"></span>
            </label>
          </div>
        `).join('')}
        ${paymentMethods.includes('stripe') ? `
          <div style="margin-top:10px; padding:10px 12px; background:var(--bg-soft); border-radius:8px;">
            <div class="label" style="margin-bottom:4px;">Online-Bezahlung je Kanal</div>
            <div class="hint" style="margin-bottom:8px;">In welchen Bestell-Kanälen darf der Gast online (Stripe) bezahlen?</div>
            ${[['takeaway','Abholung / Takeaway'], ['delivery','Lieferung'],
               ['dine-in','Am Tisch (Handy-Bestellung)']].map(([ch, label]) => {
              const chans = Array.isArray(s.stripeChannels) ? s.stripeChannels : ['takeaway', 'delivery'];
              return `
              <div class="toggle-row">
                <div style="min-width:0; flex: 1 1 auto;"><div class="label">${label}</div></div>
                <label class="toggle" style="flex: 0 0 auto;">
                  <input type="checkbox" ${chans.includes(ch) ? 'checked' : ''} onchange="updateStripeChannel('${ch}', this.checked)">
                  <span class="toggle-slider"></span>
                </label>
              </div>`;
            }).join('')}
          </div>
          <div class="hint" style="margin-top:10px; padding:10px 12px; background:var(--info-soft); border-radius:8px; border-left:3px solid var(--info); font-size:0.85rem; line-height:1.5;">
            <strong>Stripe-Setup-Checklist:</strong>
            <ol style="margin:6px 0 0 18px; padding:0;">
              <li><code>stripe_secret_key</code> (sk_test_… oder sk_live_…) in <code>public.internal_config</code> setzen.</li>
              <li><code>stripe_webhook_secret</code> (whsec_…) ebenso eintragen.</li>
              <li>Im Stripe-Dashboard: <em>Developers → Webhooks → Add endpoint</em> →<br>
                <code style="word-break:break-all;">https://taemjqkjswnohntjtimf.supabase.co/functions/v1/stripe-webhook</code><br>
                Event: <code>checkout.session.completed</code>.
              </li>
            </ol>
          </div>` : ''}
      </div>

      ${renderTableQrCard(s)}
    </div>
  `;
}

// -----------------------------------------------------------------------------
// S243 Cut A — „Bestellung am Tisch (QR)"
// -----------------------------------------------------------------------------
// Beide Werte gehen über `updateSetting` / `updateSettingClampedInt`, also den
// jsonb-Merge-Pfad. KEIN Full-PUT der settings (Memory
// feedback_gg_settings_full_put_clobber): ein zweites Gerät, das parallel eine
// andere Einstellung ändert, würde sonst überschrieben.
//
// Der Server liest dieselben Schlüssel: `selfOrderAutoEnable` in
// create_table_occupancy/checkin_reservation als Startwert des Schlosses,
// `selfOrderOverdueGraceMin` in occupancy_self_order_state. Die Defaults hier
// kommen aus core/table-qr.js und spiegeln die SQL-Defaults.
export function renderTableQrCard(settings) {
  const s = settings || {};
  const autoEnable = selfOrderSetting(s, 'selfOrderAutoEnable') !== false;
  const graceMin   = selfOrderSetting(s, 'selfOrderOverdueGraceMin');
  return `
    <div class="card">
      <h2>Bestellung am Tisch <span class="badge-soft" style="font-size:.7rem;">QR</span></h2>
      <div class="hint" style="margin-bottom:10px;">
        Gäste scannen den QR-Aufsteller am Tisch und bestellen vom eigenen Handy.
        Der Tisch ist nur scharf, solange eine Belegung läuft — der Service trägt
        die Gäste wie gewohnt als Walk-In ein oder checkt die Reservierung ein.
      </div>
      <div class="toggle-row">
        <div>
          <div class="label">Neue Belegungen automatisch freigeben</div>
          <div class="desc">Jeder neue Walk-In und jeder Reservierungs-Check-in startet mit freigegebener Handy-Bestellung. Aus: der Service gibt jeden Tisch einzeln frei.</div>
        </div>
        <label class="toggle">
          <input type="checkbox" ${autoEnable ? 'checked' : ''} onchange="updateSetting('selfOrderAutoEnable', this.checked)">
          <span class="toggle-slider"></span>
        </label>
      </div>
      <div class="field">
        <label>Sperre nach Überziehung (Min.)</label>
        <input type="number" min="0" max="240" step="5" value="${graceMin}"
               oninput="updateSettingClampedInt('selfOrderOverdueGraceMin', this.value, 0, 240, 15)"
               onchange="normalizeClampedInput(this, 0, 240, 15)">
        <div class="hint">
          So lange darf eine Belegung über ihre geplante Sitzdauer laufen, bevor die
          Handy-Bestellung automatisch sperrt. Der Schalter am Tisch bleibt davon
          unberührt — die Sperre wird bei jedem Scan neu berechnet, nicht gespeichert.
        </div>
      </div>
      <div class="hint" style="margin-top:10px; padding:10px 12px; background:var(--bg-soft); border-radius:8px;">
        🖨️ Die QR-Aufsteller druckst du unter
        <strong>System → Tischplan → „QR-Codes drucken"</strong>. Dort lässt sich der
        Code eines einzelnen Tisches auch neu erzeugen, falls ein Ausdruck abhanden kommt.
      </div>
      <div class="hint" style="margin-top:8px; padding:10px 12px; background:var(--warn-soft, var(--bg-soft)); border-radius:8px;"
           data-guest-channel-hint="table" ${guestChannelEnabled(s, 'table') ? 'hidden' : ''}>
        📴 <strong>Bestellen am Tisch ist in den Gast-Kanälen ausgeschaltet.</strong>
        Jeder Tisch-QR zeigt Gästen die Sperrseite, bis der Kanal oben wieder an ist.
      </div>
      <div class="hint" style="margin-top:8px;">
        Der Kanal „Bestellen am Tisch (QR)" in der Karte „Gast-Kanäle" steht über
        der Sperre je Tisch: ist er aus, bleibt jeder Tisch-QR gesperrt.
      </div>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// S266 — Karte „Gast-Kanäle" (phase_196): fünf Schalter, EINE Quelle
// `settings.guestChannels`. Steht in settings.js ÜBER der Sub-Tab-Leiste des
// Regeln-Tabs, also in jedem Sub-Tab sichtbar.
//
// Schreiben: `updateGuestChannel(key, on)` patcht das Objekt nicht-destruktiv
// (Merge aller fünf Schlüssel) und schreibt EINEN Key über updateSetting →
// jsonb-Merge-Pfad (Memory settings_full_put_clobber). Kein Re-Render des Tabs
// (Memory non_destructive_render): nur die abhängigen Zeilen werden per
// data-Attribut nachgezogen (Vorbestellung ausgegraut, Tisch-QR-Hinweis).
// -----------------------------------------------------------------------------
const GUEST_CHANNEL_ROWS = Object.freeze([
  { key: 'reservation',         icon: '🍽️', label: 'Reservieren',
    desc: 'Gäste können online einen Tisch reservieren.' },
  { key: 'reservationPreorder', icon: '🧾', label: 'Vorbestellung bei Reservierung', sub: true,
    desc: 'Gäste können beim Reservieren gleich Speisen vorbestellen.' },
  { key: 'takeaway',            icon: '🥡', label: 'Mitnehmen',
    desc: 'Online-Vorbestellung zur Abholung.' },
  { key: 'delivery',            icon: '🛵', label: 'Lieferung',
    desc: 'Online-Bestellung nach Hause.' },
  { key: 'table',               icon: '📱', label: 'Bestellen am Tisch (QR)',
    desc: 'Gäste bestellen per Tisch-QR-Code. Die Sperre je Tisch bleibt zusätzlich.' }
]);

export function renderGuestChannelsCard(settings) {
  const s  = settings || getState().settings || {};
  const gc = resolvedGuestChannels(s);
  const noDeliveryPath = !hasDeliveryPath(s);
  const rows = GUEST_CHANNEL_ROWS.map(r => {
    const muted = r.key === 'reservationPreorder' && !gc.reservation;
    const extra = (r.key === 'delivery' && noDeliveryPath)
      ? ` <span class="badge-soft" data-guest-channel-hint="delivery-path" style="font-size:.7rem;">Kein Liefer-Weg aktiv (Liefer-Regeln)</span>`
      : '';
    return `
        <div class="toggle-row${r.sub ? ' is-sub' : ''}${muted ? ' is-muted' : ''}" data-guest-channel-row="${r.key}">
          <div>
            <div class="label">${r.icon} ${htmlEscape(r.label)}${extra}</div>
            <div class="desc">${htmlEscape(r.desc)}</div>
          </div>
          <label class="toggle">
            <input type="checkbox" data-guest-channel="${r.key}" ${gc[r.key] ? 'checked' : ''}
                   ${muted ? 'disabled' : ''}
                   onchange="updateGuestChannel('${r.key}', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>`;
  }).join('');
  return `
    <div class="card" id="guest-channels-card">
      <h2>Gast-Kanäle <span class="badge-soft" style="font-size:.7rem;">Gastseite</span></h2>
      <div class="hint" style="margin-bottom:6px;">
        Welche Wege Gäste online nutzen können. Jeder Schalter wirkt sofort auf
        der Gastseite, dem Tisch-QR und dem Website-Button — und wird serverseitig
        erzwungen.
      </div>
      ${rows}
      <div class="hint" style="margin-top:10px;">
        Gilt nur für die Gastseite. Im Cockpit kann das Team weiterhin alle
        Bestellarten erfassen.
      </div>
    </div>
  `;
}

/**
 * Pure — nächster Zustand der fünf Schalter nach einem Klick (Vitest).
 * Immer alle fünf Schlüssel, damit das gespeicherte Objekt vollständig ist.
 */
export function nextGuestChannels(settings, key, on) {
  const cur = resolvedGuestChannels(settings);
  if (!GUEST_CHANNEL_KEYS.includes(key)) return cur;
  cur[key] = on !== false;
  return cur;
}

/** Inline-Handler der Karte — schreibt EINEN Key (jsonb-Merge) und zieht die
 *  abhängigen Zeilen nach, ohne den Tab neu zu rendern. */
export async function updateGuestChannel(key, on) {
  const state = getState();
  const next = nextGuestChannels(state.settings, key, on);
  if (typeof window.updateSetting !== 'function') return;
  await window.updateSetting('guestChannels', next);
  _patchGuestChannelDependents(next);
}

function _patchGuestChannelDependents(gc) {
  const preRow = document.querySelector('[data-guest-channel-row="reservationPreorder"]');
  if (preRow) {
    const muted = !gc.reservation;
    preRow.classList.toggle('is-muted', muted);
    const input = preRow.querySelector('input[data-guest-channel]');
    if (input) input.disabled = muted;
  }
  const tableHint = document.querySelector('[data-guest-channel-hint="table"]');
  if (tableHint) tableHint.hidden = gc.table !== false;
}

if (typeof window !== 'undefined') {
  Object.assign(window, { updateGuestChannel });
}

// -----------------------------------------------------------------------------
// Sub-Tab: Lieferungs-Regeln (S62 Hotfix-3) — eigener Sub-Sub-Tab, damit die
// 3 Liefer-Cards (Regeln + Zonen + Fahrer) volle Fenster-Breite bekommen.
// -----------------------------------------------------------------------------
export function renderRulesDeliverySubsection() {
  return `
    <div class="settings-rules-fullwidth">
      ${renderDeliveryRulesCard()}
    </div>
  `;
}
